import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Particle from "../Particle";
import Partnercard from "./PartnerCard";
import laptopImg from "../../Assets/partners.png";
import BizSkillsBadge from "../../Assets/Biz Skills Badge.png";
import TwcSeal from "../../Assets/twc-seal.png";
import Toolstack from "./Toolstack";
import Card from "react-bootstrap/Card";
import { Trans, useTranslation } from "react-i18next";
import { Handshake, Sparkles, ShieldCheck, Users } from "lucide-react";
import { Link } from "react-router-dom";

function Partners() {
  const { t } = useTranslation();
  const [showStickyCta, setShowStickyCta] = React.useState(false);
  const connectItems = Array.isArray(t("aboutPartners.connect.items", { returnObjects: true }))
    ? t("aboutPartners.connect.items", { returnObjects: true })
    : [];

  const valueStrip = [
    {
      icon: Handshake,
      title: "Mutual Value",
      body: "Partnerships designed to deliver measurable outcomes for communities and collaborators.",
    },
    {
      icon: Users,
      title: "Community Co-Creation",
      body: "Programs built with local voices so solutions are practical, inclusive, and trusted.",
    },
    {
      icon: ShieldCheck,
      title: "Responsible Innovation",
      body: "Human-centered technology adoption grounded in ethics, transparency, and long-term impact.",
    },
  ];

  const connectIcons = [Handshake, Sparkles, Users, ShieldCheck];

  React.useEffect(() => {
    const onScroll = () => {
      setShowStickyCta(window.scrollY > 300);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <Container fluid className="about-section partners-page">
      <Particle />
      <Container className="partners-container">
        <header className="partners-hero">
          <div className="partners-hero-badge">Strategic Collaboration Network</div>
          <h1 className="partners-hero-title">
            <Trans i18nKey="aboutPartners.hero.title" components={{ purple: <strong className="purple" /> }} />
          </h1>
          <p className="partners-hero-lead">
            {t("aboutPartners.hero.lead")}
          </p>
          <div className="partners-hero-accent" aria-hidden="true" />
          <div className="partners-hero-pills" aria-label="Partner strengths">
            <span className="partners-hero-pill">Public + Nonprofit + Industry</span>
            <span className="partners-hero-pill">Community-Centered Programs</span>
            <span className="partners-hero-pill">Real-World Learning Outcomes</span>
          </div>
        </header>

        <Row className="partners-top" style={{ justifyContent: "center" }}>
          <Col md={7} className="partners-left">
            <Partnercard />
          </Col>

          <Col md={5} className="partners-right">
            <div className="policy-card partners-image-card">
              <img src={laptopImg} alt={t("aboutPartners.a11y.imageAlt")} className="partners-image" />
            </div>

            <div className="policy-card partners-badge-card" aria-label="Business Skills Certificate badge">
              <div className="partners-badge-row">
                <img
                  src={BizSkillsBadge}
                  className="partners-badge-img"
                  alt="Business Skills Certificate digital badge"
                  loading="lazy"
                />
                <img
                  src={TwcSeal}
                  className="partners-badge-seal"
                  alt="TWC seal"
                  loading="lazy"
                />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="partners-value-strip g-3" aria-label="Partnership principles">
          {valueStrip.map((item) => {
            const Icon = item.icon;
            return (
              <Col key={item.title} md={4}>
                <Card className="policy-card partners-value-card h-100">
                  <Card.Body>
                    <div className="partners-value-icon-wrap" aria-hidden="true">
                      <Icon size={18} strokeWidth={2.3} />
                    </div>
                    <h3 className="partners-value-title">{item.title}</h3>
                    <p className="partners-value-body">{item.body}</p>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>

        <section className="partners-connect-section">
          <h2 className="partners-section-heading">
            <Trans i18nKey="aboutPartners.connect.heading" components={{ purple: <strong className="purple" /> }} />
          </h2>

          <Row className="partners-connect-grid g-3" aria-label={t("aboutPartners.connect.heading")}>
            {connectItems.map((item, index) => {
              const Icon = connectIcons[index % connectIcons.length];
              return (
                <Col key={item.title} md={6}>
                  <Card className="policy-card partners-connect-item-card h-100">
                    <Card.Body>
                      <div className="partners-connect-icon-wrap" aria-hidden="true">
                        <Icon size={18} strokeWidth={2.3} />
                      </div>
                      <h3 className="partners-connect-item-title">
                        <span className="purple">{item.title}</span>
                      </h3>
                      <p className="about-text partners-connect-item-body">{item.body}</p>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </section>

        <h2 className="partners-section-heading">
          <Trans i18nKey="aboutPartners.partnersHeading" components={{ purple: <strong className="purple" /> }} />
        </h2>

        <Toolstack />

        <section className="partners-cta-band" aria-label="Partner call to action">
          <div className="partners-cta-content">
            <p className="partners-cta-eyebrow">Ready to collaborate?</p>
            <h2 className="partners-cta-title">Build a meaningful partnership with Choicest Fruit</h2>
            <p className="partners-cta-lead">
              Co-create programs, strengthen local impact, and launch practical initiatives that serve your community.
            </p>
            <div className="partners-cta-actions">
              <Link to="/contact" className="btn partners-cta-btn-primary">
                Become a Partner
              </Link>
              <Link to="/programs/partner-with-us" className="btn partners-cta-btn-ghost">
                Explore Partnership Paths
              </Link>
            </div>
          </div>
        </section>
      </Container>

      <div
        className={`partners-sticky-cta${showStickyCta ? " is-visible" : ""}`}
        aria-label="Partner call to action"
        aria-hidden={!showStickyCta}
      >
        <div className="partners-sticky-cta-inner">
          <span className="partners-sticky-cta-label">Partner with Choicest Fruit</span>
          <Link to="/contact" className="btn partners-sticky-cta-btn">
            Become a Partner
          </Link>
        </div>
      </div>
    </Container>
  );
}

export default Partners;
